import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const shaderIds = new WeakMap<object, number>();
let nextShaderId = 1;

/** Un même retoucheur de shader garde le même numéro : deux clones d'un matériau se reconnaissent. */
function shaderId(hook: unknown): number {
  if (typeof hook !== "function") return 0;
  let id = shaderIds.get(hook);
  if (id === undefined) shaderIds.set(hook, (id = nextShaderId++));
  return id;
}

const RENDER_KEYS = [
  "type", "name", "side", "transparent", "opacity", "alphaTest", "depthTest", "depthWrite", "depthFunc", "colorWrite",
  "stencilWrite", "stencilRef", "stencilFunc", "stencilFuncMask", "stencilWriteMask", "stencilFail", "stencilZFail", "stencilZPass",
  "polygonOffset", "polygonOffsetFactor", "polygonOffsetUnits", "vertexColors", "flatShading", "roughness", "metalness", "blending",
] as const;

/**
 * Ce qui décide du rendu d'un matériau : deux matériaux à la même empreinte dessinent la même chose,
 * même s'ils sont des clones distincts. Les textures comptent par identité, les couleurs par valeur.
 */
export function materialFingerprint(material: THREE.Material): string {
  const record = material as unknown as Record<string, unknown>;
  const parts: unknown[] = RENDER_KEYS.map((key) => record[key]);
  for (const key of ["color", "emissive"]) {
    const color = record[key];
    parts.push(color instanceof THREE.Color ? color.getHexString() : null);
  }
  for (const key of ["map", "alphaMap", "normalMap", "emissiveMap"]) {
    const texture = record[key];
    parts.push(texture instanceof THREE.Texture ? texture.uuid : null);
  }
  parts.push(JSON.stringify(material.defines ?? null), shaderId(material.onBeforeCompile), material.customProgramCacheKey());
  return JSON.stringify(parts);
}

function compatibleAttributes(geometry: THREE.BufferGeometry): string {
  return Object.keys(geometry.attributes).sort().join(",") + (geometry.index ? "|i" : "");
}

/**
 * Fond en quelques maillages tout ce que portent ces objets immobiles : un par matériau équivalent,
 * couches et ordre de rendu, positions précalculées dans le repère de la scène. De centaines d'appels
 * de dessin, il en reste une poignée. Les objets d'origine ne sont pas modifiés.
 */
export function mergeStaticRoots(roots: THREE.Object3D[], name: string): THREE.Group {
  const groups = new Map<string, { material: THREE.Material; layers: number; renderOrder: number; geometries: THREE.BufferGeometry[] }>();
  for (const root of roots) {
    root.updateMatrixWorld(true);
    root.traverse((node) => {
      if (!(node instanceof THREE.Mesh) || node instanceof THREE.InstancedMesh || node instanceof THREE.SkinnedMesh || !node.visible) return;
      if (Array.isArray(node.material)) return;
      const geometry = (node.geometry as THREE.BufferGeometry).clone().applyMatrix4(node.matrixWorld);
      const key = `${materialFingerprint(node.material)}|${node.layers.mask}|${node.renderOrder}|${compatibleAttributes(geometry)}`;
      let group = groups.get(key);
      if (!group) groups.set(key, (group = { material: node.material, layers: node.layers.mask, renderOrder: node.renderOrder, geometries: [] }));
      group.geometries.push(geometry);
    });
  }
  const merged = new THREE.Group();
  merged.name = name;
  for (const group of groups.values()) {
    const geometry = group.geometries.length === 1 ? group.geometries[0]! : mergeGeometries(group.geometries);
    // Des attributs de même nom mais de format différent ne se fondent pas : ces morceaux restent séparés.
    const pieces = geometry ? [geometry] : group.geometries;
    if (geometry && group.geometries.length > 1) for (const part of group.geometries) part.dispose();
    for (const piece of pieces) {
      const mesh = new THREE.Mesh(piece, group.material);
      mesh.layers.mask = group.layers;
      mesh.renderOrder = group.renderOrder;
      mesh.matrixAutoUpdate = false;
      merged.add(mesh);
    }
  }
  merged.updateMatrixWorld(true);
  merged.matrixWorldAutoUpdate = false;
  return merged;
}

export function disposeMerged(group: THREE.Group): void {
  group.traverse((node) => {
    if (node instanceof THREE.Mesh) node.geometry.dispose();
  });
}
